import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import moment from "moment";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError } from "rxjs/operators";
import { PortfolioService } from "../../../portfolio/portfolio.service";
import { PortfolioModel } from "../../../../shared/models/portfolio.model";
import {
  PortfolioPollingService,
  PortfolioPollingHandle,
} from "../../../../shared/services/portfolio-polling.service";
import { PdfExportDialogComponent } from "../../../../components/pdf-export-dialog/pdf-export-dialog.component";
import {
  HbarListComponent,
  HBarRow,
} from "../../../../charts/hbar-list/hbar-list.component";
import { SparklineComponent } from "../../../../charts/sparkline/sparkline.component";
import {
  AllocRiskBarsComponent,
  AllocRiskRow,
} from "../../../../charts/alloc-risk-bars/alloc-risk-bars.component";
import { ExpCumulativeReturnChartComponent } from "../../charts/cumulative-return-chart.component";
import { ExpAnnualPerformanceChartComponent } from "../../charts/annual-performance-chart.component";
import { ExpMarginalRiskChartComponent } from "../../charts/marginal-risk-chart.component";

interface BenchmarkOption {
  name: string;
  full: string;
  group: string;
  raw: any;
}

interface BenchmarkGroup {
  label: string;
  items: BenchmarkOption[];
}

interface ConstituentRow {
  ticker: string;
  name: string;
  type: string;
  weight: number;
  cagr: number;
  volatility: number;
  riskContrib: number;
  maxDrawdown: number;
  sharpe: number;
}

@Component({
  selector: "app-exp-backtest",
  standalone: true,
  imports: [
    FormsModule,
    HbarListComponent,
    SparklineComponent,
    AllocRiskBarsComponent,
    ExpCumulativeReturnChartComponent,
    ExpAnnualPerformanceChartComponent,
    ExpMarginalRiskChartComponent,
  ],
  templateUrl: "./backtest-experience.component.html",
})
export class BacktestExperienceComponent implements OnInit, OnDestroy {
  portfolio: any = null;
  plotData: any = null;
  benchmark: any = null;
  evaluationData: any = null;

  kpis = {
    totalReturn: null as number | null,
    annualizedReturn: null as number | null,
    sharpe: null as number | null,
    maxDrawdown: null as number | null,
    cagrY5: null as number | null,
  };
  benchKpis = {
    totalReturn: null as number | null,
    annualizedReturn: null as number | null,
    sharpe: null as number | null,
    maxDrawdown: null as number | null,
  };

  valuation = {
    dividendYield: null as number | null,
    pe: null as number | null,
    ps: null as number | null,
    pb: null as number | null,
    avgMarketCap: null as number | null,
    avgEnterpriseValue: null as number | null,
    avgEBITDA: null as number | null,
  };

  risk = {
    volatility: null as number | null,
    var95: null as number | null,
    var99: null as number | null,
    alpha: null as number | null,
    beta: null as number | null,
    trackingError: null as number | null,
    informationRatio: null as number | null,
  };

  sparks = {
    total: [] as number[],
    annual: [] as number[],
    sharpe: [] as number[],
    drawdown: [] as number[],
  };

  hasAnnualData = false;
  annualMetric:
    | "annualized_return"
    | "annualized_sharpe"
    | "annualized_std_deviation" = "annualized_return";

  weightsRows: HBarRow[] = [];
  volRows: HBarRow[] = [];

  constituents: ConstituentRow[] = [];
  sortKey: keyof ConstituentRow = "weight";
  sortDir: "asc" | "desc" = "desc";

  allocVsRisk: AllocRiskRow[] = [];
  marginalRows: HBarRow[] = [];

  isLoading = true;
  isApplying = false;

  benchmarkGroups: BenchmarkGroup[] = [];
  private benchmarkByName = new Map<string, BenchmarkOption>();
  private readonly assetGroups = [
    "Europe",
    "Asia",
    "World",
    "North America",
    "The Nordics",
  ];

  editStart: string | null = null;
  editEnd: string | null = null;
  editBenchmarkName: string = "";

  private originalStart: string | null = null;
  private originalEnd: string | null = null;
  private originalBenchmark: string = "";

  private subs = new Subscription();
  private portfolioId: number | null = null;
  private pollingHandle: PortfolioPollingHandle | null = null;

  constructor(
    private route: ActivatedRoute,
    private service: PortfolioService,
    private dialog: MatDialog,
    private portfolioPollingService: PortfolioPollingService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.route.parent?.paramMap.subscribe((pm) => {
        const id = Number(pm.get("id"));
        if (id && id !== this.portfolioId) {
          this.portfolioId = id;
          this.load(id);
        }
      }) ?? Subscription.EMPTY,
    );
    this.loadBenchmarkOptions();
  }

  ngOnDestroy(): void {
    this.pollingHandle?.stop();
    this.pollingHandle = null;
    this.subs.unsubscribe();
  }

  private load(id: number): void {
    this.isLoading = true;
    this.subs.add(
      forkJoin({
        portfolio: this.service.viewItem(id).pipe(catchError(() => of(null))),
        plot: this.service
          .fetchPerformancePlot(id)
          .pipe(catchError(() => of(null))),
        evaluation: this.service
          .fetchEvaluationData(id)
          .pipe(catchError(() => of(null))),
      }).subscribe((res) => {
        const portfolio: any = res.portfolio;
        const plot: any = res.plot;
        const evaluation: any = res.evaluation;
        this.portfolio = portfolio;
        if (portfolio) {
          // Publish the loaded portfolio so shared services (e.g. company /
          // etf / fund info dialogs) can resolve tickers via portfolio$.
          this.service.selectPortfolio(new PortfolioModel(portfolio));
        }
        this.plotData = plot;
        this.evaluationData = evaluation;
        this.seedEditState();
        this.setupValuation();
        this.setupRisk();
        this.setupKpis();
        this.setupSparks();
        this.setupAnnual();
        this.setupConstituents();
        this.setupRiskContribution();
        this.isLoading = false;

        if (portfolio?.benchmark) {
          this.service
            .getBenchmark(btoa(portfolio.benchmark), id)
            .pipe(catchError(() => of(null)))
            .subscribe((b: any) => {
              this.benchmark = this.ensureBenchmarkCumulativeReturns(b);
              this.setupBenchKpis();
              this.setupAnnual();
              this.setupRisk();
            });
        }
      }),
    );
  }

  setAnnualMetric(
    m: "annualized_return" | "annualized_sharpe" | "annualized_std_deviation",
  ): void {
    this.annualMetric = m;
  }

  get hasSettingsChanges(): boolean {
    if (!this.portfolio) return false;
    return (
      this.editStart !== this.originalStart ||
      this.editEnd !== this.originalEnd ||
      this.editBenchmarkName !== this.originalBenchmark
    );
  }

  get canApply(): boolean {
    return (
      !!this.portfolio?.id &&
      !!this.editStart &&
      !!this.editEnd &&
      !this.isApplying &&
      this.hasSettingsChanges
    );
  }

  onApply(): void {
    if (!this.portfolio?.id || !this.editStart || !this.editEnd) {
      this.service.notificationService?.open?.(
        "Please fill the start and end dates.",
      );
      return;
    }
    if (this.editStart >= this.editEnd) {
      this.service.notificationService?.open?.(
        "End date must be after start date.",
      );
      return;
    }

    this.portfolio.start_date = this.editStart;
    this.portfolio.end_date = this.editEnd;
    if (
      this.editBenchmarkName &&
      this.editBenchmarkName !== this.originalBenchmark
    ) {
      this.portfolio.benchmark = this.editBenchmarkName;
    }
    this.portfolio.status = 1;
    this.portfolio.last_updated_at = "";

    this.isApplying = true;
    this.service.saveItem(this.portfolio, false).subscribe({
      next: (resp: any) => {
        if (!resp?.id) {
          this.isApplying = false;
          return;
        }
        this.pollingHandle?.stop();
        this.pollingHandle = this.portfolioPollingService.watchPortfolio(
          this.portfolio.id,
          {
            listenerKey: `backtest-experience-${this.portfolio.id}`,
            pollingInterval: 4000,
            initialDelay: 2000,
            onStatusComplete: () => {
              this.pollingHandle = null;
              this.isApplying = false;
              this.load(this.portfolio.id);
            },
            onStatusFailed: () => {
              this.pollingHandle = null;
              this.isApplying = false;
              this.service.notificationService?.openPersistent?.(
                "No feasible portfolio could be generated with the selected constraints. Please review and adjust the constraints.",
              );
            },
          },
        );
      },
      error: () => {
        this.isApplying = false;
        this.service.notificationService?.open?.(
          "Could not save changes. Please try again.",
        );
      },
    });
  }

  onResetSettings(): void {
    this.editStart = this.originalStart;
    this.editEnd = this.originalEnd;
    this.editBenchmarkName = this.originalBenchmark;
  }

  private seedEditState(): void {
    this.originalStart = this.portfolio?.start_date ?? null;
    this.originalEnd = this.portfolio?.end_date ?? null;
    this.originalBenchmark = this.portfolio?.benchmark ?? "";
    this.editStart = this.originalStart;
    this.editEnd = this.originalEnd;
    this.editBenchmarkName = this.originalBenchmark;
  }

  private loadBenchmarkOptions(): void {
    this.service
      .getBenchmarks()
      .then((obs: any) => {
        obs?.subscribe?.((groups: any[]) => {
          if (!Array.isArray(groups)) return;
          this.benchmarkGroups = groups
            .filter((g: any) => this.assetGroups.includes(g?.group))
            .map((g: any) => ({
              label: g.group,
              items: (g.items || []).map((b: any) => {
                const opt: BenchmarkOption = {
                  name: b.name,
                  full: b.name_full || b.name,
                  group: g.group,
                  raw: b,
                };
                this.benchmarkByName.set(b.name, opt);
                return opt;
              }),
            }))
            .filter((g) => g.items.length > 0);
        });
      })
      .catch(() => {
        /* noop */
      });
  }

  onExportPdf(): void {
    if (!this.portfolio?.id) return;
    this.dialog.open(PdfExportDialogComponent, {
      data: {
        portfolioId: this.portfolio.id,
        portfolioName: this.portfolio.name,
        targetPortfolio: "efficient_portfolio_max_sharpe_ratio",
      },
      maxWidth: "1200px",
      minWidth: "1100px",
    });
  }

  private setupValuation(): void {
    const s = this.portfolio?.performance_summary || {};
    const pick = (key: string): number | null => {
      const row = s[key];
      if (!row) return null;
      const val = row.Portfolio;
      return typeof val === "number" && isFinite(val) ? val : null;
    };
    this.valuation = {
      dividendYield: pick("Dividend Yield"),
      pe: pick("P/E"),
      ps: pick("P/S"),
      pb: pick("P/B"),
      avgMarketCap: pick("Market Value"),
      avgEnterpriseValue: pick("Enterprise Value"),
      avgEBITDA: pick("EBITDA"),
    };
  }

  private setupRisk(): void {
    const s = this.portfolio?.performance_summary || {};
    const portfolioSym = (this.portfolio?.symbol_performance || []).find(
      (e: any) => e.Ticker === "Portfolio",
    );
    const numOrNull = (v: any): number | null =>
      typeof v === "number" && isFinite(v) ? v : null;
    this.risk.volatility =
      numOrNull(portfolioSym?.Volatility) ??
      numOrNull(s["Annualized Std Dev"]?.Portfolio);
    this.risk.var95 =
      numOrNull(s["VaR"]?.Portfolio) ?? numOrNull(s["VaR 95"]?.Portfolio);
    this.risk.var99 =
      numOrNull(s["VaR99"]?.Portfolio) ?? numOrNull(s["VaR 99"]?.Portfolio);
    this.risk.alpha = numOrNull(portfolioSym?.Alpha);
    this.risk.beta = numOrNull(portfolioSym?.Beta);
    this.risk.trackingError = numOrNull(portfolioSym?.TrackingError);
    const annReturn =
      numOrNull(s["Annualized Return"]?.Portfolio) ??
      numOrNull(portfolioSym?.CAGR) ??
      0;
    let benchAnn = 0;
    const perfAnn = this.benchmark?.perfAnn;
    if (Array.isArray(perfAnn) && perfAnn.length) {
      const last = perfAnn[perfAnn.length - 1];
      if (last && typeof last.return === "number") benchAnn = last.return / 100;
    }
    if (this.risk.trackingError && this.risk.trackingError > 0) {
      this.risk.informationRatio =
        (annReturn - benchAnn) / this.risk.trackingError;
    }
  }

  private setupKpis(): void {
    const s = this.portfolio?.performance_summary || {};
    const cum = this.plotData?.cumulative_returns?.Portfolio;
    const portfolioSym = (this.portfolio?.symbol_performance || []).find(
      (e: any) => e.Ticker === "Portfolio",
    );
    const numOrNull = (v: any): number | null =>
      typeof v === "number" && isFinite(v) ? v : null;

    this.kpis.annualizedReturn =
      numOrNull(s["Annualized Return"]?.Portfolio) ??
      numOrNull(portfolioSym?.CAGR) ??
      this.computeAnnualizedFromCum(cum);

    this.kpis.sharpe =
      numOrNull(s["Annualized Sharpe (Rf=0%)"]?.Portfolio) ??
      numOrNull(s["Sharpe Ratio"]?.Portfolio) ??
      numOrNull(s["Sharpe"]?.Portfolio) ??
      numOrNull(portfolioSym?.Sharpe) ??
      this.computeSharpe();

    this.kpis.maxDrawdown = numOrNull(portfolioSym?.["Max Drawdown"]);

    if (Array.isArray(cum) && cum.length > 1) {
      const first = cum[0][1];
      const last = cum[cum.length - 1][1];
      this.kpis.totalReturn = first ? (last - first) / first : null;
    }

    if (Array.isArray(cum) && cum.length > 1) {
      const last = cum[cum.length - 1][1];
      const ref = cum.slice(-252 * 5)[0][1];
      if (ref) {
        this.kpis.cagrY5 = Math.pow(last / ref, 1 / 5) - 1;
      }
    }

    if (this.kpis.maxDrawdown === null && Array.isArray(cum)) {
      this.kpis.maxDrawdown = this.computeMaxDrawdown(cum);
    }
  }

  private computeAnnualizedFromCum(cum: any[] | undefined): number | null {
    if (!Array.isArray(cum) || cum.length < 2) return null;
    const first = cum[0];
    const last = cum[cum.length - 1];
    const startVal = first?.[1];
    const endVal = last?.[1];
    const startTs = first?.[0];
    const endTs = last?.[0];
    if (!startVal || !endVal || !startTs || !endTs || endTs <= startTs)
      return null;
    const years = (endTs - startTs) / (365.25 * 24 * 3600 * 1000);
    if (years <= 0) return null;
    return Math.pow(endVal / startVal, 1 / years) - 1;
  }

  private computeSharpe(): number | null {
    const ann = this.kpis.annualizedReturn;
    const vol = this.risk.volatility;
    if (ann === null || ann === undefined || !vol || vol <= 0) return null;
    return ann / vol;
  }

  private ensureBenchmarkCumulativeReturns(b: any): any {
    if (!b) return b;
    if (Array.isArray(b.cumulative_returns) && b.cumulative_returns.length)
      return b;
    if (!Array.isArray(b.benchmarkFx) || !b.benchmarkFx.length) return b;

    const portfolioStart =
      this.plotData?.cumulative_returns?.Portfolio?.[0]?.[0];
    const fx: any[] = portfolioStart
      ? b.benchmarkFx.filter(
          (d: any) => moment.utc(d.date).unix() * 1000 >= portfolioStart,
        )
      : b.benchmarkFx;
    if (fx.length < 2) return b;

    const cumulative_returns: Array<{ date: number; value: number }> = [
      { date: moment.utc(fx[0].date).unix() * 1000, value: 100 },
    ];
    let x = 1;
    for (let i = 1; i < fx.length; i++) {
      const r = fx[i].price / fx[i - 1].price - 1;
      if (!isFinite(r)) continue;
      x = x * (1 + r);
      cumulative_returns.push({
        date: moment.utc(fx[i].date).unix() * 1000,
        value: +(x * 100).toFixed(2),
      });
    }
    return { ...b, cumulative_returns };
  }

  private setupBenchKpis(): void {
    if (!this.benchmark) return;
    const cum = this.benchmark.cumulative_returns || [];
    if (Array.isArray(cum) && cum.length > 1) {
      const first = cum[0]?.value;
      const last = cum[cum.length - 1]?.value;
      this.benchKpis.totalReturn = first ? (last - first) / first : null;
    }
    const perfAnn = this.benchmark.perfAnn || [];
    if (perfAnn.length) {
      const avgReturn =
        perfAnn.reduce((a: number, b: any) => a + (b.return || 0), 0) /
        perfAnn.length;
      this.benchKpis.annualizedReturn = avgReturn / 100;
      const avgSharpe =
        perfAnn.reduce((a: number, b: any) => a + (b.sharpe || 0), 0) /
        perfAnn.length;
      this.benchKpis.sharpe = avgSharpe;
    }
    this.benchKpis.maxDrawdown = this.benchmark.maxDrawdown ?? null;
  }

  private computeMaxDrawdown(cum: any[]): number | null {
    if (!Array.isArray(cum) || cum.length < 2) return null;
    let peak = cum[0][1];
    let maxDD = 0;
    for (const p of cum) {
      const v = p[1];
      if (v > peak) peak = v;
      const dd = (v - peak) / peak;
      if (dd < maxDD) maxDD = dd;
    }
    return maxDD;
  }

  private setupSparks(): void {
    const cum = this.plotData?.cumulative_returns?.Portfolio;
    if (Array.isArray(cum) && cum.length) {
      this.sparks.total = this.sampleSeries(
        cum.map((p: any) => p[1]),
        36,
      );
      this.sparks.drawdown = this.computeDrawdownSeries(cum, 36);
    }
    const ann = this.plotData?.annualized_return?.Portfolio;
    if (Array.isArray(ann) && ann.length) {
      this.sparks.annual = this.sampleSeries(
        ann.map((p: any) => p[1] * 100),
        24,
      );
    }
    const sh = this.plotData?.annualized_sharpe?.Portfolio;
    if (Array.isArray(sh) && sh.length) {
      this.sparks.sharpe = this.sampleSeries(
        sh.map((p: any) => p[1]),
        24,
      );
    }
  }

  private computeDrawdownSeries(cum: any[], n: number): number[] {
    const out: number[] = [];
    let peak = cum[0][1];
    for (const p of cum) {
      const v = p[1];
      if (v > peak) peak = v;
      out.push(((v - peak) / peak) * 100);
    }
    return this.sampleSeries(out, n);
  }

  private setupAnnual(): void {
    const candidates: Array<
      "annualized_return" | "annualized_sharpe" | "annualized_std_deviation"
    > = ["annualized_return", "annualized_sharpe", "annualized_std_deviation"];
    this.hasAnnualData = candidates.some(
      (k) =>
        Array.isArray(this.plotData?.[k]?.Portfolio) &&
        this.plotData[k].Portfolio.length > 0,
    );
  }

  private setupConstituents(): void {
    const symbols: any[] = this.portfolio?.symbol_performance || [];
    const sec = this.portfolio?.security_overviews || {};
    const riskMap = new Map<string, number>();
    if (this.evaluationData?.risk_contribution) {
      for (const r of this.evaluationData.risk_contribution) {
        if (r.Portfolio === "baseline_portfolio") {
          riskMap.set(r.Ticker, r.risk_contribution || 0);
        }
      }
    }
    // Mirror overview.component.ts setGeneralData(): the asset type comes
    // strictly from the reference_table family on each symbol_performance
    // entry. security_overviews does not carry a reliable security_type_equity
    // for ETFs / funds, so it is only used as a name fallback.
    const resolveName = (
      e: any,
      ticker: string,
    ): { name: string; type: string } => {
      let name = "";
      let type = "";
      if (e.reference_table) {
        name = e.reference_table.company_name || "";
        type = e.reference_table.security_type_equity || "SHARE";
      } else if (e.etf_reference_table) {
        name = e.etf_reference_table.name || "";
        type = "ETF";
      } else if (e.fund_reference_table) {
        name = e.fund_reference_table.name || "";
        type = "FUND";
      } else {
        type = "SHARE";
      }
      if (!name) {
        const s = sec[ticker];
        name = s?.company_name || s?.name || ticker;
      }
      return { name, type };
    };
    const rows = symbols
      .filter((e) => e.Ticker !== "Portfolio" && (e.Weight || 0) > 0)
      .map((e) => {
        const info = resolveName(e, e.Ticker);
        const riskContrib = riskMap.get(e.Ticker) ?? 0;
        return {
          ticker: e.Ticker,
          name: info.name,
          type: info.type,
          weight: e.Weight || 0,
          cagr: e.CAGR || 0,
          volatility: e.Volatility || 0,
          riskContrib: riskContrib * (e.Weight || 0),
          maxDrawdown: e["Max Drawdown"] || 0,
          sharpe: e.Sharpe || 0,
        };
      });
    this.constituents = this.applySort(rows);
    const top = [...rows].sort((a, b) => b.weight - a.weight).slice(0, 10);
    this.weightsRows = top.map((r) => ({ label: r.ticker, value: r.weight }));
    this.volRows = top.map((r) => ({ label: r.ticker, value: r.volatility }));
  }

  setSort(key: keyof ConstituentRow): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
    } else {
      this.sortKey = key;
      // Numeric columns default to descending; the only non-numeric columns
      // (ticker / name / type) feel more natural ascending.
      this.sortDir =
        key === "ticker" || key === "name" || key === "type" ? "asc" : "desc";
    }
    this.constituents = this.applySort(this.constituents);
  }

  onConstituentClick(c: ConstituentRow): void {
    if (!c?.ticker) return;
    this.service.openCompanyInfoDialog(c.ticker);
  }

  private applySort(rows: ConstituentRow[]): ConstituentRow[] {
    const key = this.sortKey;
    const dir = this.sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });
  }

  private setupRiskContribution(): void {
    if (
      !this.evaluationData?.risk_contribution ||
      !this.portfolio?.symbol_performance
    )
      return;
    const riskMap = new Map<string, number>();
    for (const r of this.evaluationData.risk_contribution) {
      if (r.Portfolio === "baseline_portfolio")
        riskMap.set(r.Ticker, r.risk_contribution || 0);
    }
    const rows = this.portfolio.symbol_performance
      .filter((e: any) => e.Ticker !== "Portfolio" && (e.Weight || 0) > 0)
      .map((e: any) => ({
        ticker: e.Ticker,
        weight: e.Weight || 0,
        risk: riskMap.get(e.Ticker) ?? 0,
        total: e.totalReturn ?? 0,
      }))
      .sort((a: any, b: any) => b.weight - a.weight)
      .slice(0, 10);
    this.allocVsRisk = rows.map((r: any) => ({
      label: r.ticker,
      allocation: r.weight,
      risk: r.risk * r.weight,
    }));
    this.marginalRows = rows.map((r: any) => ({
      label: r.ticker,
      value: r.weight * (r.total || r.risk),
    }));
  }

  private sampleSeries(values: number[], n: number): number[] {
    if (!values.length) return [];
    if (values.length <= n) return values.slice();
    const step = values.length / n;
    const result: number[] = [];
    for (let i = 0; i < n; i++) result.push(values[Math.floor(i * step)]);
    result.push(values[values.length - 1]);
    return result;
  }

  fmtPct(v: number | null | undefined, d = 2): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    return (v * 100).toFixed(d) + "%";
  }
  fmtNum(v: number | null | undefined, d = 2): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    return v.toFixed(d);
  }
  fmtMoney(v: number | null | undefined): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    if (Math.abs(v) >= 1_000_000) return "$" + (v / 1_000_000).toFixed(2) + "T";
    if (Math.abs(v) >= 1_000) return "$" + (v / 1_000).toFixed(2) + "B";
    return "$" + v.toFixed(0) + "M";
  }
  fmtDeltaPct = (v: number | null | undefined, d = 1): string => {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    const sign = v >= 0 ? "+" : "";
    return sign + (v * 100).toFixed(d) + "%";
  };
  fmtPctAxis = (v: number) => v.toFixed(0) + "%";
  fmtCumAxis = (v: number) => v.toFixed(2);
  fmtDateAxis = (v: number | string | Date) => {
    const n = typeof v === "number" ? v : Date.parse(String(v));
    if (!isFinite(n)) return "";
    return new Date(n).toLocaleDateString(undefined, {
      month: "short",
      year: "2-digit",
    });
  };
}
